import { observer } from 'mobx-react';
import { useStore } from '../../store/store';
import { ACCOUNT_VERIF_TYPES } from '../../utils/constants/auth';
import { EXTERNAL_LINKS } from '../../utils/constants/routes';

const Footer = observer(() => {
  const { authStore, utilsStore } = useStore();
  const { user, userVerificationStatus } = authStore;

  const classNames = ['new-footer'];
  if (user && userVerificationStatus === ACCOUNT_VERIF_TYPES.APPROVED) {
    classNames.push('logged-in');
  } else {
    classNames.push('logged-out');
  }
  if (utilsStore.isMobileFooterHidden && !utilsStore.isMobileHamburgerOpen) {
    classNames.push('hidden');
  }

  return (
    <footer className={classNames.join(' ')} role="contentinfo">
      <div className="new-footer__inner">
        <div className="new-footer__brand">
          <a
            href="https://transact.digital/en/"
            aria-label="transact.digital homepage"
            target="_blank"
            rel="noopener noreferrer"
          >
            <img src="/images/logos/logo-footer.svg" alt="transact.digital Logo" />
          </a>
          <p className="new-footer__tagline">AI-native intelligence for dealmakers.</p>
        </div>

        <div className="new-footer__links">
          <div className="new-footer__col">
            <h4>Connect</h4>
            <ul>
              <li>
                <a
                  href="https://www.linkedin.com/company/transact-digital-gmbh/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  LinkedIn
                </a>
              </li>
              <li>
                <a href="https://transact.digital/" target="_blank" rel="noopener noreferrer">
                  Website
                </a>
              </li>
            </ul>
          </div>
          <div className="new-footer__col">
            <h4>Legal</h4>
            <ul>
              <li>
                <a
                  href={EXTERNAL_LINKS.TERMS_N_CONDITIONS}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Terms &amp; conditions
                </a>
              </li>
              <li>
                <a
                  href={EXTERNAL_LINKS.PRIVACY_POLICY}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Privacy policy
                </a>
              </li>
              <li>
                <a
                  href={EXTERNAL_LINKS.DATA_PROCESSING_AGREEMENT}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Data processing agreement
                </a>
              </li>
              <li>
                <a
                  href={EXTERNAL_LINKS.IMPRINT}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Imprint
                </a>
              </li>
              <li>
                <a
                  href={EXTERNAL_LINKS.THIRD_PARTY_NOTICES}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Third-party notices
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>
      <div className="new-footer__bottom">
        <span>&copy; {new Date().getFullYear()} transact.digital GmbH. All rights reserved.</span>
        <div className="new-footer__compliance">
          <div className="new-footer__badge">
            <svg
              width="40"
              height="40"
              viewBox="0 0 40 40"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <circle cx="20" cy="20" r="18" stroke="rgba(255,255,255,0.4)" strokeWidth="1.5" fill="none" />
              <circle cx="20" cy="12" r="2" fill="rgba(255,255,255,0.5)" />
              <circle cx="14" cy="15" r="1.5" fill="rgba(255,255,255,0.4)" />
              <circle cx="26" cy="15" r="1.5" fill="rgba(255,255,255,0.4)" />
              <circle cx="11" cy="20" r="1.5" fill="rgba(255,255,255,0.4)" />
              <circle cx="29" cy="20" r="1.5" fill="rgba(255,255,255,0.4)" />
              <circle cx="14" cy="25" r="1.5" fill="rgba(255,255,255,0.4)" />
              <circle cx="26" cy="25" r="1.5" fill="rgba(255,255,255,0.4)" />
              <circle cx="20" cy="28" r="1.5" fill="rgba(255,255,255,0.4)" />
              <path d="M20 20 L16 22 L18 26 L22 26 L24 22 Z" fill="none" stroke="rgba(255,255,255,0.5)" strokeWidth="1" />
              <path d="M18 18 L22 18 L23 21 L20 23 L17 21 Z" fill="rgba(74,144,217,0.4)" stroke="rgba(255,255,255,0.6)" strokeWidth="0.8" />
              <path d="M19 19.5 L20 21 L22 18.5" stroke="white" strokeWidth="1.2" fill="none" strokeLinecap="round" />
            </svg>
            <span>EU AI Act</span>
          </div>
          <div className="new-footer__badge">
            <svg
              width="40"
              height="40"
              viewBox="0 0 40 40"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <circle cx="20" cy="20" r="18" stroke="rgba(255,255,255,0.4)" strokeWidth="1.5" fill="none" />
              <rect x="14" y="17" width="12" height="10" rx="1.5" fill="none" stroke="rgba(255,255,255,0.6)" strokeWidth="1.2" />
              <path d="M16.5 17 V14 C16.5 12 18 10.5 20 10.5 C22 10.5 23.5 12 23.5 14 V17" fill="none" stroke="rgba(255,255,255,0.6)" strokeWidth="1.2" strokeLinecap="round" />
              <circle cx="20" cy="22" r="1.5" fill="rgba(74,144,217,0.6)" stroke="rgba(255,255,255,0.6)" strokeWidth="0.8" />
              <line x1="20" y1="23.5" x2="20" y2="25" stroke="rgba(255,255,255,0.6)" strokeWidth="1" strokeLinecap="round" />
            </svg>
            <span>GDPR</span>
          </div>
          <img className="new-footer__bma" src="/images/logos/bma-member.svg" alt="Member of BMA" />
          <img className="new-footer__bvk" src="/images/logos/bvk-member.png" alt="Member of BVK" />
          <img
            className="new-footer__bitkom"
            src="/images/logos/bitkom-member.svg"
            alt="Member of Bitkom"
          />
        </div>
      </div>
    </footer>
  );
});

export default Footer;
